import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, LogIn, Eye, EyeOff, X, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export default function LoginPopover() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const popRef = useRef(null);
  const btnRef = useRef(null);
  const { login } = useAuth() || {};
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-open when redirected here with ?login=1 (e.g. from a protected route)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('login') === '1') {
      setOpen(true);
      params.delete('login');
      const qs = params.toString();
      navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  // Close on outside click / ESC
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (popRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.success) {
        toast.success('Welcome back!');
        setOpen(false);
        setEmail(''); setPassword('');
        navigate('/dashboard', { replace: true });
      } else {
        toast.error(result?.error || 'Invalid credentials');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen(o => !o)}
        data-testid="nav-login"
        className="flex items-center gap-1.5 px-3 lg:px-4 py-2 rounded-full bg-[#0F172A] text-white font-medium text-xs lg:text-sm hover:bg-[#1E293B] transition-colors"
      >
        <LogIn className="w-4 h-4" />
        Sign In
      </button>

      {open && (
        <div
          ref={popRef}
          className="absolute right-0 top-[calc(100%+12px)] w-[360px] z-50 glass-pane-strong p-6 animate-in fade-in"
          data-testid="login-popover"
          style={{ animation: 'fadeIn 0.18s ease-out' }}
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-5">
            <div>
              <h3 className="font-heading text-lg font-semibold text-[#0F172A] tracking-tight">Welcome back</h3>
              <p className="text-xs text-[#64748B] mt-0.5">Sign in to your CRATELAB account</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="w-7 h-7 rounded-full hover:bg-[#F1F5F9] text-[#94A3B8] hover:text-[#0F172A] flex items-center justify-center"
              data-testid="login-popover-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <form onSubmit={submit} className="space-y-3" data-testid="login-popover-form">
            <div className="relative">
              <Mail className="w-4 h-4 text-[#94A3B8] absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
                data-testid="login-popover-email"
                className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-[#E2E8F0] bg-white text-sm placeholder:text-[#94A3B8] focus:border-[#2563FF] focus:ring-2 focus:ring-[#2563FF]/15 outline-none"
              />
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 text-[#94A3B8] absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                autoComplete="current-password"
                data-testid="login-popover-password"
                className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-[#E2E8F0] bg-white text-sm placeholder:text-[#94A3B8] focus:border-[#2563FF] focus:ring-2 focus:ring-[#2563FF]/15 outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPw(s => !s)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md flex items-center justify-center text-[#94A3B8] hover:text-[#0F172A] hover:bg-[#F1F5F9]"
                tabIndex={-1}
                data-testid="login-popover-toggle-password"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <div className="flex items-center justify-between text-xs">
              <Link to="/forgot-password" onClick={() => setOpen(false)} className="text-[#64748B] hover:text-[#2563FF]" data-testid="login-popover-forgot">
                Forgot password?
              </Link>
              <Link to="/signup" onClick={() => setOpen(false)} className="text-[#2563FF] font-medium hover:underline" data-testid="login-popover-signup">
                Create account →
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading || !email || !password}
              data-testid="login-popover-submit"
              className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            >
              {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</>) : 'Sign In'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
